import { computed, type IObservableValue, observable, runInAction } from "mobx"
import { useEffect } from "react"

import { useModel } from "@/hooks/useModel.ts"
import { Model } from "@/lib/Model.ts"

function lerp(from: number, to: number, t: number): number {
  return from * (1 - t) + to * t
}

export type StreamedTextOptions = {
  symbolsPerSecond: number
  symbolsPerSecondSmoothness: number
  updateIntervalSeconds: number
  maxCatchupTimeSeconds: number
}

export class StreamedTextModel extends Model {
  private readonly optionsOrDefault: StreamedTextOptions
  private symbolsPerSecond: number
  private targetSymbolsPerSecond: number
  private accumulatedDeltaTime: number = 0
  private text = observable.box("")
  private isStreamCompleted = observable.box(false)
  private timeout: number | null = null
  private isLoopRunning = false
  private iterationTime: number | null = null
  private readonly cutoffIndex = observable.box(0)

  readonly result = computed(() => ({
    streamedText: this.text.get().substring(0, this.cutoffIndex.get()),
    isStreamCompleted: this.cutoffIndex.get() >= this.text.get().length && this.isStreamCompleted.get()
  }))

  constructor(
    text: string,
    isNew: boolean,
    isStreamCompleted: boolean,
    options?: Partial<StreamedTextOptions>
  ) {
    super("StreamedTextModel")
    this.optionsOrDefault = {
      symbolsPerSecond: 50,
      symbolsPerSecondSmoothness: 0.01,
      updateIntervalSeconds: 0.02,
      maxCatchupTimeSeconds: 2,
      ...options
    }
    this.symbolsPerSecond = this.optionsOrDefault.symbolsPerSecond
    this.targetSymbolsPerSecond = this.optionsOrDefault.symbolsPerSecond
    this.lifetime.add(() => {
      if (this.timeout) {
        cancelAnimationFrame(this.timeout)
      }
    }, "animationRef")
    if (!isNew) {
      this.cutoffIndex.set(text.length)
    }
    this.consumeText(text, isStreamCompleted)
  }

  private readonly iteration = (deltaTime: number) => {
    runInAction(() => {
      this.accumulatedDeltaTime += deltaTime
      this.symbolsPerSecond = lerp(this.symbolsPerSecond, this.targetSymbolsPerSecond, this.optionsOrDefault.symbolsPerSecondSmoothness)

      if (this.accumulatedDeltaTime > this.optionsOrDefault.updateIntervalSeconds * 1000) {
        const consumeSymbols = this.accumulatedDeltaTime * this.symbolsPerSecond / 1000
        const actuallyConsumedSymbols = Math.floor(consumeSymbols)
        if (actuallyConsumedSymbols > 0) {
          const deltaTimeRefund = (consumeSymbols - actuallyConsumedSymbols) / this.symbolsPerSecond * 1000
          // console.log(`
          //   Streaming symbols/s = ${this.symbolsPerSecond.toFixed(2)},
          //   target = ${this.targetSymbolsPerSecond.toFixed(2)},
          //   consume = ${actuallyConsumedSymbols} symbols,
          //   deltaTime = ${this.accumulatedDeltaTime.toFixed(1)},
          //   deltaTimeRefund = ${deltaTimeRefund}
          // `)
          this.cutoffIndex.set(Math.min(this.text.get().length, this.cutoffIndex.get() + actuallyConsumedSymbols))
          this.accumulatedDeltaTime = deltaTimeRefund
        }
      }
    })
  }

  private readonly loop = () => {
    if (this.cutoffIndex.get() >= this.text.get().length && this.isStreamCompleted.get()) {
      this.accumulatedDeltaTime = 0
      this.iterationTime = null
      this.isLoopRunning = false
      return
    }

    const currentIterationTime = new Date().getTime()
    if (this.iterationTime !== null) {
      this.iteration(currentIterationTime - this.iterationTime)
    } else {
      this.iteration(0)
    }
    this.iterationTime = currentIterationTime
    this.timeout = requestAnimationFrame(() => {
      this.loop()
    })
  }

  readonly consumeText = (text: string, isStreamCompleted: boolean) => {
    runInAction(() => {
      const cutoffIndex = this.cutoffIndex.get()
      if (text.length > cutoffIndex) {
        this.targetSymbolsPerSecond = Math.max((text.length - cutoffIndex) / this.optionsOrDefault.maxCatchupTimeSeconds, this.optionsOrDefault.symbolsPerSecond)
      } else if (text.length < cutoffIndex) {
        this.symbolsPerSecond = this.optionsOrDefault.symbolsPerSecond
        this.targetSymbolsPerSecond = this.optionsOrDefault.symbolsPerSecond
      }
      this.text.set(text)
      this.isStreamCompleted.set(isStreamCompleted)

      if (!this.isLoopRunning) {
        this.isLoopRunning = true
        this.loop()
      }
    })
  }
}

export function useStreamedText(
  text: string,
  isNew: boolean,
  isStreamCompleted: boolean,
  options?: Partial<StreamedTextOptions>
): IObservableValue<{ streamedText: string, isStreamCompleted: boolean }> {

  const streamedTextModel = useModel(() => (
    new StreamedTextModel(text, isNew, isStreamCompleted, options)
  ), [options?.symbolsPerSecond, options?.symbolsPerSecondSmoothness, options?.updateIntervalSeconds, options?.maxCatchupTimeSeconds])

  useEffect(() => {
    streamedTextModel.consumeText(text, isStreamCompleted)
  }, [streamedTextModel, text, isStreamCompleted])

  return streamedTextModel.result
}
